import AHDate from "_shared/dates/date";

export default class Year {
  public static fromInput(input: string | number | null | undefined): Year | undefined {
    if (input === null || input === undefined || input === '') return undefined;
    return new Year(input.toString());
  }

  public static isValid(
    value: string | number,
    options: { cannotBeInPast?: boolean, cannotBeInFuture?: boolean } = {}
  ): value is number {
    try {
      Year.validate(value.toString(), options);
      value = Number.parseInt(value.toString());
      return !Number.isNaN(value) && value >= 0;
    } catch (error) {
      if (error instanceof InvalidYearError)
        return false;
      throw error;
    }
  }

  public static validate(
    value: string,
    options: {
      cannotBeInPast?: boolean,
      cannotBeInFuture?: boolean
    } = {}
  ): void {
    if (!value) {
      throw new InvalidYearError({ errorMessage: 'cannot be empty.', value });
    }

    const validYearRegExp = new RegExp(/^\d{4}$/g);
    const isValidFormat = validYearRegExp.test(value);
    const isInThePast = Number(value) < new AHDate().year;
    const isInTheFuture = Number(value) > new AHDate().year;

    const { cannotBeInPast, cannotBeInFuture } = options;
    if (!isValidFormat)
      throw new InvalidYearError({ errorMessage: 'must be a 4-digit year format (YYYY).', value });
    if (cannotBeInPast && isInThePast)
      throw new InvalidYearError({ errorMessage: 'cannot be in the past.', value });
    if (cannotBeInFuture && isInTheFuture)
      throw new InvalidYearError({ errorMessage: 'cannot be in the future.', value });
  }

  public readonly value: number;
  public get string(): string { return this.value.toString() }
  public get number(): number { return this.value }

  constructor(
    value: number | string,
    options: {
      cannotBeInPast?: boolean,
      cannotBeInFuture?: boolean
    } = {}
  ) {
    Year.validate(value.toString(), options);
    this.value = Number(value);
  }

  public valueOf(): number {
    return this.value;
  }

  public toString(): string {
    return this.value.toString();
  }

  public toNumber(): number {
    return Number(this.value);
  }

  public isEqualTo(other: Year | null | undefined): boolean {
    return this.value === other?.value;
  }
}

export class InvalidYearError extends Error {
  public readonly errorMessage: string;
  public readonly value: string;
  constructor(args: {
    errorMessage: string,
    value: string
  }) {
    const { errorMessage, value } = args;
    super(`Invalid year${errorMessage.length ? ': ' + errorMessage : ''}`);
    this.errorMessage = errorMessage;
    this.value = value;
  }
}
